import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { getCommunityGallery } from '../../services/computeWallet';

const FILTER_TABS = [
  { id: 'all', label: 'All Creations', icon: 'Grid3X3', color: '#6366F1' },
  { id: 'video', label: 'Videos', icon: 'Video', color: '#6366F1' },
  { id: 'music', label: 'Music', icon: 'Music', color: '#8B5CF6' },
  { id: 'game', label: 'Games', icon: 'Gamepad2', color: '#10B981' },
];

const SORT_OPTIONS = [
  { id: 'created_at', label: 'Newest' },
  { id: 'like_count', label: 'Most Liked' },
  { id: 'view_count', label: 'Most Viewed' },
  { id: 'remix_count', label: 'Most Remixed' },
];

const TYPE_COLORS = {
  video: '#6366F1',
  music: '#8B5CF6',
  game: '#10B981',
};

const TYPE_ICONS = {
  video: 'Video',
  music: 'Music',
  game: 'Gamepad2',
};

function GalleryCard({ item, onRemix, onViewPrompt }) {
  const typeColor = TYPE_COLORS?.[item?.generationType] || '#6366F1';
  const typeIcon = TYPE_ICONS?.[item?.generationType] || 'Layers';

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col transition-all duration-200 hover:scale-[1.01]"
      style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-sm)' }}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video overflow-hidden" style={{ background: 'var(--color-muted)' }}>
        {item?.thumbnailUrl ? (
          <img
            src={item?.thumbnailUrl}
            alt={`${item?.title} — ${item?.generationType} creation by ${item?.creator?.name}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name={typeIcon} size={32} color={typeColor} />
          </div>
        )}
        {/* Type badge */}
        <div
          className="absolute top-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
          style={{ background: `${typeColor}dd`, color: 'white', fontFamily: 'var(--font-caption)' }}
        >
          <Icon name={typeIcon} size={10} color="white" />
          {item?.generationType}
        </div>
        {/* Remix badge */}
        {item?.allowRemix && (
          <div
            className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(16,185,129,0.85)', color: 'white', fontFamily: 'var(--font-caption)' }}
          >
            <Icon name="GitFork" size={10} color="white" />
            Remixable
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-3 flex flex-col gap-2 flex-1">
        <div>
          <h3 className="text-sm font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>
            {item?.title || 'Untitled Creation'}
          </h3>
          {item?.description && (
            <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
              {item?.description}
            </p>
          )}
        </div>

        {/* Creator */}
        <div className="flex items-center gap-1.5">
          <div className="w-5 h-5 rounded-full flex items-center justify-center" style={{ background: `${typeColor}20` }}>
            {item?.creator?.avatar ? (
              <img src={item?.creator?.avatar} alt={item?.creator?.name} className="w-full h-full rounded-full object-cover" />
            ) : (
              <Icon name="User" size={10} color={typeColor} />
            )}
          </div>
          <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
            {item?.creator?.name}
          </span>
        </div>

        {/* Stats */}
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            <Icon name="Eye" size={11} />
            {item?.viewCount?.toLocaleString() || 0}
          </span>
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            <Icon name="Heart" size={11} />
            {item?.likeCount?.toLocaleString() || 0}
          </span>
          <span className="flex items-center gap-1 text-xs" style={{ color: 'var(--color-muted-foreground)' }}>
            <Icon name="GitFork" size={11} />
            {item?.remixCount?.toLocaleString() || 0}
          </span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 mt-auto pt-1">
          {item?.allowRemix && (
            <button
              onClick={() => onRemix?.(item)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: `${typeColor}12`, color: typeColor, border: `1px solid ${typeColor}30`, fontFamily: 'var(--font-caption)' }}
            >
              <Icon name="GitFork" size={12} color={typeColor} />
              Remix
            </button>
          )}
          {item?.prompt && (
            <button
              onClick={() => onViewPrompt?.(item)}
              className="flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'var(--color-muted)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)', fontFamily: 'var(--font-caption)' }}
            >
              <Icon name="Copy" size={12} />
              Use Prompt
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function CommunityGallery() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [activeFilter, setActiveFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [copiedPrompt, setCopiedPrompt] = useState(false);

  const loadGallery = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCommunityGallery({
        generationType: activeFilter === 'all' ? null : activeFilter,
        sortBy,
        limit: 24,
      });
      setItems(data);
    } catch (err) {
      setError('Could not load community gallery. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeFilter, sortBy]);

  useEffect(() => {
    loadGallery();
  }, [loadGallery]);

  const handleRemix = useCallback((item) => {
    navigate('/creator-dashboard', {
      state: {
        remixItem: item,
        mode: item?.generationType === 'game' ? 'text-to-game' : item?.generationType === 'music' ? 'text-to-music' : 'music-to-video',
      },
    });
  }, [navigate]);

  const handleViewPrompt = useCallback((item) => {
    setSelectedPrompt(item);
  }, []);

  const handleCopyPrompt = useCallback(async (prompt) => {
    try {
      await navigator?.clipboard?.writeText(prompt);
      setCopiedPrompt(true);
      setTimeout(() => setCopiedPrompt(false), 2000);
    } catch {
      setCopiedPrompt(false);
    }
  }, []);

  const handleUsePrompt = useCallback((item) => {
    navigate('/creator-dashboard', {
      state: {
        remixItem: item,
        mode: item?.generationType === 'game' ? 'text-to-game' : item?.generationType === 'music' ? 'text-to-music' : 'music-to-video',
        prefillPrompt: item?.prompt,
      },
    });
  }, [navigate]);

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-background)' }}>
      {/* Header */}
      <div
        className="sticky top-0 z-30 px-4 py-3"
        style={{ background: 'var(--color-background)', borderBottom: '1px solid var(--color-border)', backdropFilter: 'blur(12px)' }}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-8 h-8 rounded-xl flex items-center justify-center transition-colors hover:opacity-70"
              style={{ background: 'var(--color-muted)' }}
            >
              <Icon name="ArrowLeft" size={16} color="var(--color-foreground)" />
            </button>
            <div>
              <h1 className="text-lg font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Community Gallery</h1>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Explore, remix, and get inspired by community creations</p>
            </div>
          </div>
          {isAuthenticated && (
            <button
              onClick={() => navigate('/creator-dashboard')}
              className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
              style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
            >
              <Icon name="Plus" size={14} color="white" />
              Create
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Filters */}
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          {/* Type filter tabs */}
          <div className="flex items-center gap-2 flex-wrap">
            {FILTER_TABS?.map((tab) => {
              const active = activeFilter === tab?.id;
              return (
                <button
                  key={tab?.id}
                  onClick={() => setActiveFilter(tab?.id)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all"
                  style={{
                    background: active ? `${tab?.color}15` : 'var(--color-card)',
                    color: active ? tab?.color : 'var(--color-muted-foreground)',
                    border: `1px solid ${active ? tab?.color + '40' : 'var(--color-border)'}`,
                    fontFamily: 'var(--font-caption)',
                  }}
                >
                  <Icon name={tab?.icon} size={12} color={active ? tab?.color : 'var(--color-muted-foreground)'} />
                  {tab?.label}
                </button>
              );
            })}
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Sort:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="text-xs rounded-xl px-2 py-1.5 border"
              style={{ background: 'var(--color-card)', color: 'var(--color-foreground)', borderColor: 'var(--color-border)', fontFamily: 'var(--font-caption)' }}
            >
              {SORT_OPTIONS?.map((opt) => (
                <option key={opt?.id} value={opt?.id}>{opt?.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 rounded-2xl flex items-center justify-center animate-pulse" style={{ background: 'var(--color-primary)' }}>
                <Icon name="Layers" size={20} color="white" />
              </div>
              <p className="text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Loading gallery...</p>
            </div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center gap-3 py-16">
            <Icon name="AlertCircle" size={32} color="#EF4444" />
            <p className="text-sm" style={{ color: '#EF4444', fontFamily: 'var(--font-caption)' }}>{error}</p>
            <button onClick={loadGallery} className="text-xs px-3 py-1.5 rounded-xl" style={{ background: 'var(--color-muted)', color: 'var(--color-foreground)' }}>Retry</button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && items?.length === 0 && (
          <div className="flex flex-col items-center gap-4 py-20">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
              <Icon name="Layers" size={28} color="var(--color-muted-foreground)" />
            </div>
            <div className="text-center">
              <p className="text-base font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>No creations yet</p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>
                Be the first to publish your creation to the community gallery!
              </p>
            </div>
            {isAuthenticated && (
              <button
                onClick={() => navigate('/creator-dashboard')}
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
              >
                <Icon name="Plus" size={14} color="white" />
                Create Something
              </button>
            )}
          </div>
        )}

        {/* Gallery grid */}
        {!loading && !error && items?.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items?.map((item) => (
              <GalleryCard
                key={item?.id}
                item={item}
                onRemix={handleRemix}
                onViewPrompt={handleViewPrompt}
              />
            ))}
          </div>
        )}
      </div>

      {/* Prompt modal */}
      {selectedPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
          onClick={() => setSelectedPrompt(null)}
        >
          <div
            className="w-full max-w-md rounded-2xl p-5 flex flex-col gap-4"
            style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)' }}
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h3 className="text-base font-bold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Generation Prompt</h3>
              <button onClick={() => setSelectedPrompt(null)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-muted)' }}>
                <Icon name="X" size={14} color="var(--color-muted-foreground)" />
              </button>
            </div>
            <div className="p-3 rounded-xl" style={{ background: 'var(--color-muted)', border: '1px solid var(--color-border)' }}>
              <p className="text-sm" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-body)', lineHeight: 1.6 }}>
                {selectedPrompt?.prompt || 'No prompt available.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleCopyPrompt(selectedPrompt?.prompt)}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-80"
                style={{ background: 'var(--color-muted)', color: 'var(--color-foreground)', border: '1px solid var(--color-border)', fontFamily: 'var(--font-heading)' }}
              >
                <Icon name={copiedPrompt ? 'Check' : 'Copy'} size={14} color={copiedPrompt ? 'var(--color-success)' : 'var(--color-foreground)'} />
                {copiedPrompt ? 'Copied!' : 'Copy Prompt'}
              </button>
              <button
                onClick={() => { handleUsePrompt(selectedPrompt); setSelectedPrompt(null); }}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90"
                style={{ background: 'var(--color-primary)', color: 'white', fontFamily: 'var(--font-heading)' }}
              >
                <Icon name="Zap" size={14} color="white" />
                Use & Generate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
